import {createNextState} from '@reduxjs/toolkit';
import chunk from 'lodash/chunk';
import get from 'lodash/get';

import {TData, TPayload} from 'src/components/List';
import {EChannelType, TItemId} from 'src/constants';
import {EWebNotificationType, isMatchNotificationQuery} from 'src/containers/Notifications';
import {CACHE_LIFETIME, ECacheTag, listenerMiddleware, rootApi} from 'src/models';
import {mapNullToUndefined, mapUndefinedToNull} from 'src/utils';

import {
  EEventType,
  EFlowStepType,
  TActionStep,
  TCycles,
  TFlow,
  TFlowStatus,
  TFlowStep,
  TListFlow,
  TPaths,
  TSchedule,
  TSendMessageAction,
} from './types';

type TFlowMutation = {
  name: string;
  description: string;
  onlyOnes: boolean;
  subjectId?: number;
  startAt?: Date;
  endAt?: Date;
  schedule?: TSchedule;
};

enum EFlowErrorCodes {
  CYCLES_FOUND = 'step-cycles-found',
  NON_TEMPLATE_MESSAGES_WITHOUT_IN_MESSAGE_FOUND = 'non-template-messages-without-in-message-found',
  NON_TEMPLATE_MESSAGES_WITH_DAY_WINDOW_FOUND = 'non-template-messages-with-day-window-found',
  HAS_ACTIVE_FLOW_WITH_IN_MESSAGE_FOR_SAME_SUBJECT = 'flow.has-active-flow-with-in-message-for-same-subject',
}

type TCyclesError = {
  title: EFlowErrorCodes.CYCLES_FOUND;
  args: {
    cycles: TCycles;
  };
};

type TTemplateMessageError = {
  title:
    | EFlowErrorCodes.NON_TEMPLATE_MESSAGES_WITH_DAY_WINDOW_FOUND
    | EFlowErrorCodes.NON_TEMPLATE_MESSAGES_WITHOUT_IN_MESSAGE_FOUND;
  args: {
    paths: TPaths;
  };
};

type THasActiveFlowError = {
  title: EFlowErrorCodes.HAS_ACTIVE_FLOW_WITH_IN_MESSAGE_FOR_SAME_SUBJECT;
  args: {
    name: string;
  };
};

type TReportGeneration = {
  name: string;
};

enum EReportGenerationStatus {
  ACCEPTED = 'ACCEPTED',
}

type TIntegration = {
  isActive: boolean;
};

const isFlowAction = (step: TAnyObject): step is TActionStep => {
  return (
    'type' in step &&
    step.type === EFlowStepType.ACTION &&
    !!get(step, 'payload.action.messageContent.contentHolder.buttons')
  );
};

const isMessageAction = (action?: TAnyObject): action is TSendMessageAction => {
  return !!action && 'messageContent' in action;
};

const transformFlowStep = (step: TFlowStep): TFlowStep => {
  if (!isFlowAction(step)) {
    return step;
  }

  const action = step.payload.action;

  if (!isMessageAction(action)) {
    return step;
  }

  return createNextState(step, (draft) => {
    if (
      isFlowAction(draft) &&
      isMessageAction(draft.payload.action) &&
      draft.payload.action.messageContent?.contentHolder
    ) {
      draft.payload.action.messageContent.contentHolder.buttons =
        action.messageContent?.contentHolder?.buttons?.flat(1) ?? [];
    }

    return draft;
  });
};

const prepareFlowStepForSave = (step: TFlowStep) => {
  if (!isFlowAction(step)) {
    return step;
  }

  const action = step.payload.action;

  if (!isMessageAction(action)) {
    return step;
  }

  const buttons = action.messageContent?.contentHolder?.buttons ?? [];

  if (!buttons.length) {
    return step;
  }

  return createNextState(step, (draft) => {
    if (
      isFlowAction(draft) &&
      isMessageAction(draft.payload.action) &&
      draft.payload.action.messageContent?.contentHolder
    ) {
      if (action.messageContent?.channelType === EChannelType.MAX_BOT) {
        draft.payload.action.messageContent.contentHolder.buttons = chunk(
          buttons,
          buttons.length > 5 ? 3 : 1,
        ) as TAnyObject[];
      }

      if (action.messageContent?.channelType === EChannelType.WHATSAPP) {
        // All buttons in 1 row
        draft.payload.action.messageContent.contentHolder.buttons = [[...buttons]] as TAnyObject[];
      }
    }
  });
};

const prepareFlowStepsForSave = (steps: TFlowStep[]) =>
  mapUndefinedToNull(
    steps.map((step) => ({...prepareFlowStepForSave(step), meta: JSON.stringify(step.meta)})),
  );

const flowApi = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    createFlow: builder.mutation<TFlow, TFlowMutation>({
      query: (data) => ({
        method: 'POST',
        url: '/flow',
        data: mapUndefinedToNull(data),
        meta: {isShowError: true},
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    saveFlow: builder.mutation<TFlow, {id: TFlow['id']; data: TFlowMutation}>({
      query: ({id, data}) => ({
        method: 'PUT',
        url: `/flow/${id}`,
        data: mapUndefinedToNull(data),
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'List:notification.saveSuccess',
        },
      }),
      invalidatesTags: (_, __, {id}) => [{type: ECacheTag.FLOW, id}],
    }),
    activateFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/activate`,
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.FLOW, id}],
    }),
    deactivateFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/deactivate`,
        meta: {
          isShowError: true,
          isShowSuccess: true,
          successMessageKey: 'Triggers:notification.deactivateSuccess',
        },
      }),
      invalidatesTags: (_, __, id) => [{type: ECacheTag.FLOW, id}],
    }),
    copyFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'POST',
        url: `/flow/${id}/copy`,
        meta: {
          isShowError: true,
        },
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    deleteFlow: builder.mutation<TFlow, TFlow['id']>({
      query: (id) => ({
        method: 'DELETE',
        url: `/flow/${id}/delete`,
        meta: {
          isShowError: true,
          errorMessageKey: 'List:errors.failedToDeleteItem',
          isShowSuccess: true,
          successMessageKey: 'List:notification.deleteSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.FLOW],
    }),
    getFilteredFlows: builder.query<
      TFlow[],
      {
        subjectId?: TItemId;
        statuses?: TFlowStatus[];
        conditionType?: EEventType;
      }
    >({
      query: (data) => ({
        method: 'POST',
        url: '/flow/filter',
        data,
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.FLOW];
        }

        return [ECacheTag.FLOW, ...result.map(({id}) => ({id, type: ECacheTag.FLOW}))];
      },
    }),
    getFlows: builder.query<TData<TListFlow>, TPayload>({
      query: ({tenantId, ...data}) => ({
        method: 'POST',
        url: tenantId ? `/flow/list/tenant/${tenantId}` : '/flow/list',
        data,
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.FLOW];
        }

        return [ECacheTag.FLOW, ...result.content.map(({id}) => ({id, type: ECacheTag.FLOW}))];
      },
    }),
    getFlow: builder.query<TFlow, {flowId: TFlow['id']; tenantId?: TItemId}>({
      query: ({flowId, tenantId}) => ({
        method: 'GET',
        url: tenantId ? `/flow/${flowId}/tenant/${tenantId}` : `/flow/${flowId}`,
      }),
      providesTags: (_, __, {flowId}) => [{id: flowId, type: ECacheTag.FLOW}],
    }),
    getFlowSteps: builder.query<TFlowStep[], {flowId: TFlow['id']; tenantId?: TItemId}>({
      query: ({flowId, tenantId}) => ({
        method: 'GET',
        url: tenantId ? `/flow/${flowId}/steps/tenant/${tenantId}` : `/flow/${flowId}/steps`,
      }),
      providesTags: [ECacheTag.FLOW_STEPS],
      transformResponse: (data: Array<TFlowStep & {meta: string}>) =>
        mapNullToUndefined(
          data.map((step) => ({
            ...transformFlowStep(step),
            meta: JSON.parse(step.meta || '{}'),
          })),
        ),
    }),
    validateFlowSteps: builder.mutation<
      {isValid: boolean},
      {flowId: TFlow['id']; tenantId?: TItemId; steps: TFlowStep[]}
    >({
      query: ({flowId, tenantId, steps}) => ({
        method: 'POST',
        data: prepareFlowStepsForSave(steps),
        url: tenantId
          ? `/flow/${flowId}/steps/validate/tenant/${tenantId}`
          : `/flow/${flowId}/steps/validate`,
      }),
    }),
    saveFlowSteps: builder.mutation<
      TFlowStep[],
      {id: TFlow['id']; steps: TFlowStep[]; ignoreCycles?: boolean}
    >({
      query: ({id, steps, ignoreCycles}) => ({
        method: 'POST',
        url: `/flow/${id}/steps`,
        data: prepareFlowStepsForSave(steps),
        params: {ignoreCycles},
        meta: {
          isShowSuccess: true,
          successMessageKey: 'Triggers:notification.saveSuccess',
        },
      }),
      invalidatesTags: [ECacheTag.FLOW_STEPS],
    }),
    generateFlowReport: builder.mutation<
      {status: EReportGenerationStatus; name: string},
      {flowId: TFlow['id']; locale: string}
    >({
      query: ({flowId, locale}) => ({
        method: 'POST',
        url: '/report/flow/download',
        data: {
          flowId,
          locale,
        },
        meta: {
          isShowError: true,
        },
      }),
    }),
    getFlowReportGenerations: builder.query<TReportGeneration[], void>({
      query: () => ({
        method: 'GET',
        url: '/report/flow/downloads',
        meta: {isShowError: true},
      }),
      providesTags: [ECacheTag.FLOW_MESSAGE_REPORT_GENERATIONS],
    }),
    getChatCenterIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/chat-center/integration/subject/${subjectId}/status`,
      }),
    }),
    getJivoIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/jivo/integration/subject/${subjectId}/status`,
      }),
    }),
    getCallbackIntegration: builder.query<TIntegration, TItemId>({
      query: (subjectId) => ({
        method: 'GET',
        url: `/callback/in-message/subject/${subjectId}/status`,
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: flowApi.endpoints.generateFlowReport.matchFulfilled,
  effect: ({payload}, listenerApi) => {
    if (payload.status === EReportGenerationStatus.ACCEPTED) {
      listenerApi.dispatch(
        flowApi.util.updateQueryData('getFlowReportGenerations', undefined, (draft) =>
          draft ? [...draft, {name: payload.name}] : [{name: payload.name}],
        ),
      );
    }
  },
});

listenerMiddleware.startListening({
  matcher: isMatchNotificationQuery(
    EWebNotificationType.FlowMessageReportSuccess,
    EWebNotificationType.FlowMessageReportFailed,
  ),
  effect: (_, listenerApi) => {
    listenerApi.dispatch(flowApi.util.invalidateTags([ECacheTag.FLOW_MESSAGE_REPORT_GENERATIONS]));
  },
});

export const {
  useGetFlowsQuery,
  useGetFlowQuery,
  useGetFlowStepsQuery,
  useCreateFlowMutation,
  useSaveFlowStepsMutation,
  useActivateFlowMutation,
  useDeactivateFlowMutation,
  useCopyFlowMutation,
  useDeleteFlowMutation,
  useSaveFlowMutation,
  useGenerateFlowReportMutation,
  useGetFlowReportGenerationsQuery,
  useValidateFlowStepsMutation,
  useGetJivoIntegrationQuery,
  useGetCallbackIntegrationQuery,
  useGetChatCenterIntegrationQuery,
  useLazyGetFilteredFlowsQuery,
  useGetFilteredFlowsQuery,
  endpoints,
} = flowApi;

export {
  TFlowMutation,
  TCyclesError,
  EFlowErrorCodes,
  TReportGeneration,
  TTemplateMessageError,
  THasActiveFlowError,
};
